import axios from 'axios';
import { invalidateOnMutation } from '../lib/cache';

const getApiBase = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '') + '/api';
  }
  return '/api';
};

const API = getApiBase();

// 30s default timeout so slow/failing backend calls don't hang the UI
axios.defaults.timeout = 30000;

// Automatic in-memory cache invalidation on successful mutations
axios.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      invalidateOnMutation(response.config?.url || '');
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// Auth APIs
export const authAPI = {
  login: (username, password) => axios.post(`${API}/auth/login`, { username, password }),
  getMe: () => axios.get(`${API}/auth/me`),
  changePassword: (oldPassword, newPassword) => axios.post(`${API}/auth/change-password`, { old_password: oldPassword, new_password: newPassword }),
  getStaff: () => axios.get(`${API}/auth/staff`),
  createStaff: (data) => axios.post(`${API}/auth/staff`, data),
  updateStaff: (id, data) => axios.put(`${API}/auth/staff/${id}`, data),
  deleteStaff: (id) => axios.delete(`${API}/auth/staff/${id}`)
};

// Settings APIs
export const settingsAPI = {
  get: () => axios.get(`${API}/settings`),
  update: (data) => axios.put(`${API}/settings`, data),
  uploadImage: (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', type);
    return axios.post(`${API}/settings/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getEmailSettings: () => axios.get(`${API}/settings/email`),
  updateEmailSettings: (data) => axios.put(`${API}/settings/email`, data),
  testEmail: () => axios.post(`${API}/settings/email/test`)
};

// Bank APIs
export const bankAPI = {
  getAll: (params) => axios.get(`${API}/banks`, { params }),
  getOne: (id) => axios.get(`${API}/banks/${id}`),
  create: (data) => axios.post(`${API}/banks`, data),
  update: (id, data) => axios.put(`${API}/banks/${id}`, data),
  delete: (id) => axios.delete(`${API}/banks/${id}`),
  getCascadeInfo: (id) => axios.get(`${API}/banks/${id}/cascade-info`),
  toggleStatus: (id) => axios.patch(`${API}/banks/${id}/toggle-status`),
  exportExcel: () => axios.get(`${API}/banks/export-excel`, { responseType: 'blob' }),
  downloadTemplate: () => axios.get(`${API}/banks/sample-template`, { responseType: 'blob' }),
  importExcel: (file, defaultCategory = 'auto') => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/banks/import-excel?default_category=${encodeURIComponent(defaultCategory)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Agent APIs
export const agentAPI = {
  getAll: (params) => axios.get(`${API}/agents`, { params }),
  getOne: (id) => axios.get(`${API}/agents/${id}`),
  create: (data) => axios.post(`${API}/agents`, data),
  update: (id, data) => axios.put(`${API}/agents/${id}`, data),
  delete: (id) => axios.delete(`${API}/agents/${id}`),
  getCascadeInfo: (id) => axios.get(`${API}/agents/${id}/cascade-info`),
  toggleStatus: (id) => axios.patch(`${API}/agents/${id}/toggle-status`),
  exportExcel: () => axios.get(`${API}/agents/export-excel`, { responseType: 'blob' }),
  downloadTemplate: () => axios.get(`${API}/agents/sample-template`, { responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/agents/import-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAadhaar: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/agents/${id}/upload-aadhaar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadDL: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/agents/${id}/upload-dl`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  documentUrl: (id, type) => `${API}/agents/${id}/document/${type}`,
  // Agent Advances
  recordAdvance: (id, data) => axios.post(`${API}/agents/${id}/advances`, data),
  getAdvances: (id) => axios.get(`${API}/agents/${id}/advances`),
  getAllAdvances: (params) => axios.get(`${API}/agents/advances/all`, { params }),
  downloadAdvanceSlip: (advanceId) => axios.get(`${API}/agents/advances/slip/${advanceId}`, { responseType: 'blob' }),
  settleAdvance: (advanceId, month, year) =>
    axios.patch(`${API}/agents/advances/${advanceId}/settle?month=${month || ''}&year=${year || ''}`),
  deleteAdvance: (advanceId) => axios.delete(`${API}/agents/advances/${advanceId}`),
};

// Template APIs
export const templateAPI = {
  downloadVisitCertificate: () =>
    axios.get(`${API}/templates/visit-certificate`, { responseType: 'blob' }),
  downloadAuthorityLetter: ({ agentId, bankId, photo, signature, attachAadhaar }) => {
    const fd = new FormData();
    fd.append('agent_id', agentId);
    fd.append('bank_id', bankId);
    fd.append('photo', photo);
    if (signature) fd.append('signature', signature);
    fd.append('attach_aadhaar', attachAadhaar ? 'true' : 'false');
    return axios.post(`${API}/templates/authority-letter`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 60000,
    });
  },
  downloadSubmissionCoverLetter: ({ bankId, month, year, refNo, letterDate, invoiceIds }) => {
    const fd = new FormData();
    fd.append('bank_id', bankId);
    fd.append('month', String(month));
    fd.append('year', String(year));
    if (refNo) fd.append('ref_no', refNo);
    if (letterDate) fd.append('letter_date', letterDate);
    if (invoiceIds) fd.append('invoice_ids', invoiceIds);
    return axios.post(`${API}/templates/submission-cover-letter`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 60000,
    });
  },
};

// Certificate APIs
export const certificateAPI = {
  generateLinks: (data) => axios.post(`${API}/certificates/generate-links`, data),
  generateSingle: (bankId, month, year, periodType = 'full', periodFrom, periodTo) => {
    const params = new URLSearchParams({
      bank_id: bankId,
      month: String(month),
      year: String(year),
      period_type: periodType,
    });
    if (periodFrom) params.append('period_from', periodFrom);
    if (periodTo) params.append('period_to', periodTo);
    return axios.post(`${API}/certificates/generate-single?${params.toString()}`);
  },
  delete: (certId) => axios.delete(`${API}/certificates/${certId}`),
  uploadAdminProof: (certId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/certificates/${certId}/upload-proof`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  uploadProof: (token, file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return axios.post(`${API}/public/upload/${token}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  regenerate: (id) => axios.post(`${API}/certificates/${id}/regenerate`),
  getAll: (params) => axios.get(`${API}/certificates`, { params }),
  getOne: (id) => axios.get(`${API}/certificates/${id}`),
  approve: (id, data) => axios.put(`${API}/certificates/${id}/approve`, data),
  reject: (id, data) => axios.put(`${API}/certificates/${id}/reject`, data),
  editCount: (id, count) => axios.put(`${API}/certificates/${id}/edit-count?visit_count_final=${count}`),
  sendEmailReminder: (id) => axios.post(`${API}/certificates/${id}/send-email-reminder`),
  exportExcel: (month, year) => axios.get(`${API}/certificates/export-excel?month=${month}&year=${year}`, { responseType: 'blob' })
};

// CMS Client Directory APIs
export const cmsClientAPI = {
  listBranches: () => axios.get(`${API}/cms-clients/branches`),
  getBranch: (id) => axios.get(`${API}/cms-clients/branches/${id}`),
  createBranch: (data) => axios.post(`${API}/cms-clients/branches`, data),
  updateBranch: (id, data) => axios.put(`${API}/cms-clients/branches/${id}`, data),
  deleteBranch: (id) => axios.delete(`${API}/cms-clients/branches/${id}`),
  addClient: (branchId, data) => axios.post(`${API}/cms-clients/branches/${branchId}/clients`, data),
  updateClient: (branchId, clientId, data) => axios.put(`${API}/cms-clients/branches/${branchId}/clients/${clientId}`, data),
  deleteClient: (branchId, clientId) => axios.delete(`${API}/cms-clients/branches/${branchId}/clients/${clientId}`),
  downloadPdf: (branchId) => axios.get(`${API}/cms-clients/branches/${branchId}/pdf`, { responseType: 'blob' }),
  seedExcel: () => axios.post(`${API}/cms-clients/seed-excel`),
};

// Public Upload APIs (no auth)
export const publicAPI = {
  getUploadInfo: (token) => axios.get(`${API}/public/upload/${token}`),
  uploadCertificate: (token, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return axios.post(`${API}/public/upload/${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Invoice APIs
export const invoiceAPI = {
  preview: (data) => axios.post(`${API}/invoices/preview`, data),
  generate: (data) => axios.post(`${API}/invoices/generate`, data),
  generateBulk: (data) => axios.post(`${API}/invoices/generate-bulk`, data),
  getAll: (params) => axios.get(`${API}/invoices`, { params }),
  getOne: (id) => axios.get(`${API}/invoices/${id}`),
  update: (id, data) => axios.put(`${API}/invoices/${id}`, data),
  finalize: (id) => axios.put(`${API}/invoices/${id}/finalize`),
  cancel: (id, reason) => axios.put(`${API}/invoices/${id}/cancel?reason=${encodeURIComponent(reason)}`),
  checkNumber: (number) => axios.get(`${API}/invoices/check-number/${encodeURIComponent(number)}`),
  sendEmail: (id) => axios.post(`${API}/invoices/${id}/send-email`),
  bulkEmail: (month, year) => axios.post(`${API}/invoices/bulk-email?month=${month}&year=${year}`),
  downloadPDF: (id, withSupport = true, withSeal = false) => axios.get(`${API}/invoices/${id}/pdf?with_support=${withSupport}&with_seal=${withSeal}`, { responseType: 'blob' }),
  downloadPDFNoSupport: (id, withSeal = false) => axios.get(`${API}/invoices/${id}/pdf?with_support=false&with_seal=${withSeal}`, { responseType: 'blob' }),
  downloadSupportingPDF: (id, withSeal = false) => axios.get(`${API}/invoices/${id}/supporting-pdf?with_seal=${withSeal}`, { responseType: 'blob' }),
  downloadBundlePDF: (id, withSeal = false) => axios.get(`${API}/invoices/${id}/bundle-pdf?with_seal=${withSeal}`, { responseType: 'blob' }),
  downloadAllPDF: (month, year) => axios.get(`${API}/invoices/download-all-pdf?month=${month}&year=${year}`, { responseType: 'blob' }),
  bulkPdf: (month, year, withSupport = true, withSeal = false) => axios.get(`${API}/invoices/bulk-pdf?month=${month}&year=${year}${withSupport ? '' : '&with_support=false'}&with_seal=${withSeal}`, { responseType: 'blob', timeout: 120000 }),
  bulkPdfNoSupport: (invoiceIds, withSeal = false) => axios.post(`${API}/invoices/bulk-download-no-support?with_seal=${withSeal}`, invoiceIds, { responseType: 'blob', timeout: 120000 }),
  fixWboDescriptions: () => axios.post(`${API}/invoices/fix-wbo-descriptions`),
  reissue: (id, reasonPayload) => {
    const r = typeof reasonPayload === 'object' ? (reasonPayload?.reason || 'Duplicate requested') : (reasonPayload || 'Duplicate requested');
    return axios.post(`${API}/invoices/${id}/reissue?reason=${encodeURIComponent(r)}`);
  },
  addNote: (id, note, reminderDate) => axios.post(`${API}/invoices/${id}/notes?note=${encodeURIComponent(note)}${reminderDate ? `&reminder_date=${reminderDate}` : ''}`),
  getNotes: (id) => axios.get(`${API}/invoices/${id}/notes`),
  deleteNote: (id, noteId) => axios.delete(`${API}/invoices/${id}/notes/${noteId}`),
  uploadSupportingDoc: (id, file, label) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/invoices/${id}/supporting-docs${label ? `?doc_label=${encodeURIComponent(label)}` : ''}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getSupportingDocs: (id) => axios.get(`${API}/invoices/${id}/supporting-docs`),
  deleteSupportingDoc: (id, docId) => axios.delete(`${API}/invoices/${id}/supporting-docs/${docId}`),
  updateCourier: (id, { courier_name, tracking_number, tracking_url }) =>
    axios.post(`${API}/invoices/${id}/courier?courier_name=${encodeURIComponent(courier_name)}&tracking_number=${encodeURIComponent(tracking_number)}${tracking_url ? `&tracking_url=${encodeURIComponent(tracking_url)}` : ''}`),
  getDueReminders: () => axios.get(`${API}/invoices/due-reminders`),
};

// Payment APIs
export const paymentAPI = {
  getBankPayments: (params) => axios.get(`${API}/payments/banks`, { params }),
  updateBankPayment: (id, data) => axios.put(`${API}/payments/banks/${id}`, data),
  sendReminder: (id) => axios.post(`${API}/payments/banks/${id}/send-reminder`),
  bulkReminder: (month, year) => axios.post(`${API}/payments/banks/bulk-reminder?month=${month}&year=${year}`),
  bulkReminderPreview: (month, year) => axios.get(`${API}/payments/banks/bulk-reminder/preview?month=${month}&year=${year}`),
  getAgentPayments: (params) => axios.get(`${API}/payments/agents`, { params }),
  calculateAgentPayments: (month, year) => axios.post(`${API}/payments/agents/calculate?month=${month}&year=${year}`),
  updateAgentPayment: (id, data) => axios.put(`${API}/payments/agents/${id}`, data),
  payAgentForInvoice: (data) => axios.post(`${API}/payments/agents/pay-for-invoice`, data),
  exportAgentsExcel: (params) => axios.get(`${API}/payments/agents/export-excel`, { params, responseType: 'blob' }),
  getSummary: (params) => axios.get(`${API}/payments/summary`, { params }),
  getGrouped: (params) => axios.get(`${API}/payments/grouped`, { params }),
  getInvoiceHistory: (invoiceId) => axios.get(`${API}/payments/history/${invoiceId}`),
  downloadAgentPayTemplate: (month, year) =>
    axios.get(`${API}/payments/agents/template?month=${month}&year=${year}`, { responseType: 'blob', timeout: 60000 }),
  uploadAgentPayments: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/payments/agents/bulk-upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  downloadHistoricalTemplate: () =>
    axios.get(`${API}/payments/historical/template`, { responseType: 'blob', timeout: 60000 }),
  uploadHistoricalPayments: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/payments/historical/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

// Dashboard & Reports APIs
export const dashboardAPI = {
  get: (params) => axios.get(`${API}/dashboard`, { params }),
  getActivity: (limit = 20) => axios.get(`${API}/dashboard/activity?limit=${limit}`)
};

// Admin APIs
export const adminAPI = {
  resetDemoData: (confirm = 'CONFIRM') => axios.post(`${API}/admin/reset-demo-data`, { confirm }),
  resetEverything: (confirm = 'CONFIRM') => axios.post(`${API}/admin/reset-everything`, { confirm }),
  seedSampleData: () => axios.post(`${API}/admin/seed-sample-data`),
  seedTestData: () => axios.post(`${API}/admin/seed-test-data`, { confirm: 'WIPE' }),
  exportBackup: () => axios.get(`${API}/admin/export-backup`, { responseType: 'blob' }),
};

export const reportAPI = {
  getGSTSummary: (month, year) => axios.get(`${API}/reports/gst-summary?month=${month}&year=${year}`, { responseType: 'blob' }),
  getMonthlyPnL: (month, year) => axios.get(`${API}/reports/monthly-pnl?month=${month}&year=${year}`),
  getCertificateStatus: (month, year) => axios.get(`${API}/reports/certificate-status?month=${month}&year=${year}`),
  getAgentPerformance: (month, year) => axios.get(`${API}/reports/agent-performance?month=${month}&year=${year}`)
};
